import PropTypes from "prop-types";
import React from "react";
import WebFont from "webfontloader";
import themeDataProvider from "./themeDataProvider.js";
import colorHelper from "./utils/colorHelper.js";

class ThemeManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      theme_options: {},
      loaded: false
    };
  }

  UNSAFE_componentWillUpdate(nextProps, nextState) {
    if (this.props.setGradient) {
      if (
        nextState.theme_options.colorBgPrimary !==
        this.state.theme_options.colorBgPrimary
      ) {
        this.props.setGradient(nextState.theme_options.colorBgPrimary);
      }
    }
  }

  componentDidMount() {
    if (this.props.theme && typeof this.props.theme !== "undefined") {
      this.fetchThemeData(this.props.theme);
    } else {
      this.setState({ loaded: true });
    }
  }

  fetchThemeData(theme) {
    // new format : organization_id#theme_id
    const themeArray = theme.split("#");
    if (themeArray.length !== 2) {
      // theme format is not match, so ignore loading theme
      this.setState({
        theme_options: {},
        loaded: true
      });
      return;
    }

    theme =
      '{"organization_id":"' +
      themeArray[0] +
      '", "theme_id":"' +
      themeArray[1] +
      '"}';

    let maxAgeMinute = 0.2;
    let cache_ttl_seconds = 60 * maxAgeMinute;
    const themeUrl = process.env.REACT_APP_THEME_EDITOR_URL;
    let fetchParams = {
      data: theme,
      cache_ttl: cache_ttl_seconds,
      timeout: 3000
    };

    themeDataProvider
      .getThemeData(themeUrl, fetchParams, maxAgeMinute)
      .then(result => {
        console.log("fetchThemeData > result = ", result);
        if (!result || result === null || result.id === "default") {
          throw new Error("Theme not found");
        }

        this.setState({
          theme_options: result,
          loaded: true
        });
      })
      .catch(error => {
        console.warn(
          "Failed to get data for " + themeUrl + ", params = ",
          fetchParams,
          error
        );
        this.setState({
          theme_options: {},
          loaded: true
        });
      });
  }

  extractGoogleFont(googleFontUrl) {
    let customGoogleFont = googleFontUrl
      .split("family=")[1]
      .split('" rel=')[0]
      .split(":");
    let customGoogleFontName = customGoogleFont[0].replace(/\+/g, " ");
    return customGoogleFontName;
  }

  getCustomFontTitle(fontUrl, fontTitle) {
    if (fontUrl.indexOf("fonts.googleapis.com") !== -1) {
      fontTitle = this.extractGoogleFont(fontUrl);
    }
    return fontTitle;
  }

  loadCustomFont(fontUrl, fontTitle) {
    if (fontUrl.indexOf("fonts.googleapis.com") !== -1) {
      WebFont.load({
        google: {
          families: [this.extractGoogleFont(fontUrl)]
        }
      });
    } else {
      WebFont.load({
        custom: {
          families: [fontTitle],
          urls: [fontUrl]
        }
      });
    }
  }

  applyCustomStyle() {
    let customStyle;
    let themeKeys = Object.keys(this.state.theme_options) || [];
    if (
      typeof this.state.theme_options === "undefined" ||
      !this.state.theme_options ||
      this.state.theme_options === {} ||
      themeKeys.length === 0
    ) {
      return "";
    }

    let theme = this.state.theme_options;
    let fontUrlHeading =
      typeof theme.fontUrlHeading !== "undefined" ? theme.fontUrlHeading : "";
    let fontUrlBody =
      typeof theme.fontUrlBody !== "undefined" ? theme.fontUrlBody : "";
    let fontHeading =
      typeof theme.fontHeading !== "undefined" ? theme.fontHeading : "";
    let fontBody =
      typeof theme.fontHeading !== "undefined" ? theme.fontBody : "";
    let linkColor =
      typeof theme.colorTextLink !== "undefined" ? theme.colorTextLink : "";
    let linkOpacity;

    if (linkColor !== "") {
      linkOpacity = 1;
    }

    // Webfont Loader
    switch (fontHeading) {
      case "Default":
        break;
      case "Custom Heading":
      case "Custom":
        this.loadCustomFont(fontUrlHeading, "Custom Heading");
        fontHeading = this.getCustomFontTitle(fontUrlHeading, "Custom Heading");
        break;
      default:
        WebFont.load({
          google: {
            families: [fontHeading]
          }
        });
    }

    switch (fontBody) {
      case "Default":
        break;
      case "Custom Body":
      case "Custom":
        this.loadCustomFont(fontUrlBody, "Custom Body");
        fontBody = this.getCustomFontTitle(fontUrlBody, "Custom Body");
        break;
      default:
        WebFont.load({
          google: {
            families: [fontBody]
          }
        });
    }

    customStyle = (
      <style>
        {`
      @font-face {
        font-family: "Custom Heading";
        src: url("` +
          fontUrlHeading +
          `") format("woff2");
      }

      @font-face {
        font-family: "Custom Body";
        src: url("` +
          fontUrlBody +
          `") format("woff2");
      }

      .font-heading {
        font-family: ` +
          fontHeading +
          `;
      }

      .font-body {
        font-family: ` +
          fontBody +
          `;
      }

      .color-text-link,
      a {
        color: ` +
          linkColor +
          ` !important;
        opacity: ${linkOpacity ? linkOpacity : 1};
      }

      .color-bg-primary {
        background-color: ` +
          theme.colorBgPrimary +
          ` !important;
      }

      .color-bg-primary--darker20 {
        background-color: ` +
          colorHelper.colorLuminance(theme.colorBgPrimary, -0.2) +
          ` !important;
      }

      .color-bg-primary--text {
        color: ` +
          theme.colorBgPrimary +
          ` !important;
      }

      .color-bg-primary--fill {
        fill: ` +
          theme.colorBgPrimary +
          ` !important;
      }

      .color-bg-primary--border {
        border-color: ` +
          colorHelper.colorLuminance(theme.colorBgPrimary, -0.2) +
          ` !important;
      }

      .color-text-heading {
        color: ` +
          theme.colorTextHeading +
          ` !important;
      }

      .color-text-heading--bg {
        background-color: ` +
          theme.colorTextHeading +
          ` !important;
        border-color: ` +
          theme.colorTextHeading +
          ` !important;
      }

      .color-text-heading--bg-opacity {
        background-color: ` +
          colorHelper.hexToRgb(theme.colorTextHeading, 0.2) +
          ` !important;
        border-color: ` +
          colorHelper.hexToRgb(theme.colorTextHeading, 0.2) +
          ` !important;
      }

      .color-text-heading--stroke {
        stroke: ` +
          theme.colorTextHeading +
          ` !important;
      }

      .color-text-heading--fill {
        fill: ` +
          theme.colorTextHeading +
          ` !important;
      }

      .color-text-heading--border {
        border-color: ` +
          theme.colorTextHeading +
          ` !important;
      }

      .color-text-heading--pseudo:before {
        background-color: ` +
          theme.colorTextHeading +
          ` !important;
      }

      .color-text-heading--pseudo:after {
        background-color: ` +
          theme.colorTextHeading +
          ` !important;
      }

      .color-text-body {
        color: ` +
          theme.colorTextBody +
          ` !important;
      }

      .color-text-body--bg {
        background-color: ` +
          theme.colorTextBody +
          ` !important;
      }

      .color-text-body--border {
        border-color: ` +
          theme.colorTextBody +
          ` !important;
      }

      .color-text-body--border-opacity {
        border-color: ` +
          colorHelper.hexToRgb(theme.colorTextBody, 0.2) +
          ` !important;
      }

      .color-text-bg {
        color: ` +
          theme.colorBgPrimary +
          ` !important;
      }

    `}
      </style>
    );

    return customStyle;
  }

  render() {
    let customStyle = "";
    let themeClassName = "preloadState";
    let checkBgColor = colorHelper.checkContrast(
      this.state.theme_options.colorBgPrimary
    );
    if (this.state.loaded) {
      themeClassName = "";
      if (this.props.theme) {
        themeClassName = checkBgColor ? "bg-" + checkBgColor : "";
        customStyle = this.applyCustomStyle();
      }
      return (
        <div id="theme-container" className={themeClassName}>
          {this.props.children}
          {customStyle}
        </div>
      );
    } else {
      return <div id="theme-container" className={themeClassName} />;
    }
  }
}

ThemeManager.propTypes = {
  setGradient: PropTypes.func,
  theme: PropTypes.string,
  children: PropTypes.object
};

export default ThemeManager;
